import { slicedToArray as _slicedToArray, extends as _extends, toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import { ModalHeader } from '../ModalHeader.js';
import { Button } from '../Button/index.js';
import { i18n } from '../../i18n/translate.js';
import { noop } from '../../utils/noop.js';
import kebabCase from 'lodash/kebabCase.js';
import { Stack } from '@chakra-ui/react';
import { ConstructionCard } from '../ConstructionCard/component.js';
import { isFilterActive, getInitialFilters, filtersArrayToKeyValueObject, clearAllFilters, removeFilterFromCollection } from '../Filters/functions/index.js';
import { FilterChips } from '../Filters/FilterChips/index.js';
import { DateFilter } from '../Filters/DateFilter.js';

var FilterModal = function FilterModal(_ref) {
  var applyButtonText = _ref.applyButtonText,
      backdropClassName = _ref.backdropClassName,
      cancelButtonText = _ref.cancelButtonText,
      canClose = _ref.canClose,
      className = _ref.className,
      container = _ref.container,
      filterConfig = _ref.filterConfig,
      initialFilters = _ref.initialFilters,
      modalProps = _ref.modalProps,
      onSelectFilters = _ref.onSelectFilters,
      _ref$onClearFilters = _ref.onClearFilters,
      onClearFilters = _ref$onClearFilters === void 0 ? noop : _ref$onClearFilters,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? noop : _ref$onClose,
      show = _ref.show,
      title = _ref.title;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      filters = _useState2[0],
      setFilters = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      expandedFilters = _useState4[0],
      setExpandedFilters = _useState4[1];

  var activeFilters = filters.filter(function (filter) {
    return isFilterActive(filter);
  });
  useEffect(function () {
    var filters = getInitialFilters(filterConfig, initialFilters);
    setFilters(filters); // Ignore hooks warning as this logic is intended to be run on mount only.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var handleApplyFilters = function handleApplyFilters() {
    onSelectFilters(filtersArrayToKeyValueObject(filters));
  };

  var clearFilters = function clearFilters() {
    var newFilters = clearAllFilters(filters);
    setFilters(newFilters);
    onClearFilters();
  };

  var removeFilter = function removeFilter(filterToRemove) {
    var newFilters = removeFilterFromCollection(filterToRemove, filters);
    setFilters(newFilters);
  };

  var onTogglePanel = function onTogglePanel(expanded, expandedFilter) {
    if (expanded) {
      setExpandedFilters([].concat(_toConsumableArray(expandedFilters), [expandedFilter]));
    } else {
      setExpandedFilters(expandedFilters.filter(function (filter) {
        return filter !== expandedFilter;
      }));
    }
  };

  var handleFilterChange = function handleFilterChange(updatedFilter, value) {
    var newFilters = filters.map(function (filter) {
      if (filter.name === updatedFilter.name) {
        if (filter.type === DateFilter) {
          filter.value = _objectSpread2(_objectSpread2({}, filter.value), value);
        } else {
          filter.value = value;
        }
      }

      return filter;
    });
    setFilters(newFilters);
  };

  var renderFooter = function renderFooter() {
    return /*#__PURE__*/React.createElement(Modal.Footer, {
      className: "sticky-footer"
    }, activeFilters.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "selected-filters"
    }, /*#__PURE__*/React.createElement("p", {
      className: "title"
    }, i18n.t('Selected Filters')), /*#__PURE__*/React.createElement(FilterChips, {
      filters: activeFilters,
      onRemoveFilter: removeFilter
    })), /*#__PURE__*/React.createElement("div", {
      className: "footer-buttons"
    }, activeFilters.length > 0 && /*#__PURE__*/React.createElement(Button, {
      onClick: clearFilters,
      style: "link",
      className: "clear-filters",
      "data-action": "clear-filters",
      icon: "xmark",
      label: i18n.t('Clear Filters')
    }), /*#__PURE__*/React.createElement(Button, {
      style: "secondary",
      onClick: onClose,
      "data-action": "cancel-filters"
    }, cancelButtonText), /*#__PURE__*/React.createElement(Button, {
      style: "primary",
      onClick: handleApplyFilters,
      "data-action": "apply-filters"
    }, applyButtonText)));
  };

  var renderBody = function renderBody() {
    return /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Stack, null, filters.map(function (filter) {
      var FilterComponent = filter.type;
      var selectedCount = Array.isArray(filter.value) ? filter.value.length : null;
      var selectedText = selectedCount !== null ? "".concat(selectedCount, " ").concat(i18n.t('selected')) : '';
      return /*#__PURE__*/React.createElement(ConstructionCard, {
        key: "panel_".concat(filter.name),
        className: "filter-panel bordered p-l-20",
        onToggle: function onToggle(expanded) {
          return onTogglePanel(expanded, filter.name);
        },
        "data-action": "open-".concat(kebabCase(filter.name), "-panel"),
        title: filter.label,
        customActionBar: selectedText,
        overrideOverflowY: FilterComponent.displayName === 'DateFilter' ? 'visible' : 'scroll'
      }, /*#__PURE__*/React.createElement(FilterComponent, _extends({}, filter, {
        key: filter.name,
        name: filter.label,
        onChange: function onChange(value) {
          return handleFilterChange(filter, value);
        },
        disabled: !!filter.disabled,
        isWrapper: true,
        clearable: false
      })));
    })));
  };

  return /*#__PURE__*/React.createElement(Modal, {
    animation: false,
    backdrop: canClose ? true : 'static',
    backdropClassName: backdropClassName || '',
    container: container,
    size: modalProps.bsSize,
    className: "filter-modal sticky-footer ".concat(className || ''),
    id: modalProps.id,
    keyboard: canClose,
    onHide: onClose,
    show: show
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    onCancel: onClose,
    canClickOff: canClose,
    title: title
  }), renderBody(), renderFooter());
};

FilterModal.displayName = "FilterModal";
FilterModal.propTypes = {
  applyButtonText: PropTypes.string,
  backdropClassName: PropTypes.string,
  cancelButtonText: PropTypes.string,
  canClose: PropTypes.bool,
  className: PropTypes.string,
  container: PropTypes.object,
  filterConfig: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    getChipDisplay: PropTypes.func,
    getSelectionDisplay: PropTypes.func,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
  })),
  initialFilters: PropTypes.object,
  modalProps: PropTypes.shape({
    id: PropTypes.string,
    bsSize: PropTypes.oneOf(['sm', 'md', 'lg'])
  }),
  onClearFilters: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSelectFilters: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  show: PropTypes.bool
};
FilterModal.defaultProps = {
  applyButtonText: i18n.t('Apply Filters'),
  cancelButtonText: i18n.t('Cancel'),
  canClose: true,
  filterConfig: [],
  initialFilters: {},
  modalProps: {
    id: 'filterModal',
    bsSize: 'md'
  },
  show: true
};
export { FilterModal };